import { useContext, useEffect, useState } from 'react';
import { useSearchParams, NavLink as RouterLink } from 'react-router-dom';
// material
import { styled } from '@mui/material/styles';
import { Box, Grid, Container, Typography, Card } from '@mui/material';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
// context
import ReportProvider from '../../contexts/Report';
import { EventContext } from '../../contexts/Event';
// helpers
import { getTimeZoneDate, getTimeZoneGTM } from '../../helpers/Util';
// components
import Page from '../../components/Page';
import EventCalendar from '../../components/_dashboard/date/EventCalendar';
import EventCalendarTabs from '../../components/_dashboard/date/EventCalendarTabs';
import DateRange from '../../components/_dashboard/date/DateRange';
import {
  AppBuyers,
  AppOrders,
  AppTickets,
  AppSales,
  AppRefundedSales,
  AppPaymentMethodsList,
  AppDailySalesChart,
  AppLastOrdersList,
  AppCancelledOrders,
  AppTicketsList,
  AppSectionsList,
  AppRegionList
} from '../../components/_dashboard/app';

// ----------------------------------------------------------------------

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  '&:before': {
    display: 'none'
  }
}));

const AccordionSummary = styled((props) => <MuiAccordionSummary {...props} />)(() => ({
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(0deg)'
  }
}));

// TODO: Distribuir em mais components
export default function DashboardSale() {
  const { event } = useContext(EventContext);

  const [searchParams, setSearchParams] = useSearchParams();
  const [currentDate, setCurrentDate] = useState(false);

  useEffect(() => {
    setCurrentDate('');
    const date = searchParams.get('eventDate');

    if (date) {
      setCurrentDate(
        `${getTimeZoneDate({ date })
          .tz('Europe/Lisbon')
          .format('ddd, DD MMM')} (GTM${getTimeZoneGTM({ date })})`
      );
    }
  }, [searchParams]);

  const handleClearFilter = () => {
    setSearchParams(updateSearchParams('eventDate', '', true));
    setSearchParams(updateSearchParams('start', '', true));
    setSearchParams(updateSearchParams('end', '', true));
  };

  // TODO: Colocar essa função no helper
  const updateSearchParams = (key, value, remove) => {
    const params = new URL(document.location).searchParams;

    const updatedSearchParams = new URLSearchParams(params.toString());

    if (remove) {
      updatedSearchParams.delete(key);
    } else {
      updatedSearchParams.set(key, value);
    }
    return updatedSearchParams.toString();
  };

  if (!event.calendar.schedule.length) {
    return (
      <Container maxWidth="xl">
        <Alert severity="info">
          It looks like you have an incomplete event registration. {'Got to '}
          <Link to={`/event/edit?eventId=${event._id}`} color="inherit" component={RouterLink}>
            event page details / edit
          </Link>
          {' and complete de required fields!'}
        </Alert>
      </Container>
    );
  }

  return (
    <ReportProvider>
      <Page title="Dashboard" path="/dashboard/sales">
        <Container maxWidth="xl">
          <Box sx={{ pb: { xs: 1, md: 4 } }}>
            <Accordion>
              <AccordionSummary
                expandIcon={
                  <>
                    {(searchParams.get('eventDate') ||
                      searchParams.get('start') ||
                      searchParams.get('end')) && (
                      <Button
                        color="error"
                        variant="outlined"
                        onClick={handleClearFilter}
                        sx={{ mr: 1 }}
                      >
                        Clear
                      </Button>
                    )}
                    <Button variant="outlined" endIcon={<FilterAltIcon />}>
                      Filter
                    </Button>
                  </>
                }
                aria-controls="filter-content"
                id="filter-header"
                sx={{ border: 'none', px: 0 }}
              >
                <Typography variant="h4">
                  Dashboard Sales
                  <Typography
                    component="span"
                    variant="h6"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    {currentDate}
                  </Typography>
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ pb: 2, display: { xs: 'block', md: 'flex' } }}>
                  <Box sx={{ ml: 'auto', mb: { xs: 1, md: 0 } }}>
                    {event.calendar.recurrence.frequency === 'no-repeat' &&
                    event.calendar.schedule.length > 1 ? (
                      <EventCalendarTabs />
                    ) : (
                      <EventCalendar />
                    )}
                  </Box>
                  <Box sx={{ ml: { xs: 0, md: 2 } }}>
                    <DateRange title="Sales Range" />
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <AppSales />
            </Grid>
            <Grid item xs={12} md={4}>
              <AppTickets />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box mb={2}>
                <AppBuyers />
              </Box>
              <Box mb={2}>
                <AppOrders />
              </Box>
              <Box mb={2}>
                <AppRegionList />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, pt: 0 }}>
                <AppTicketsList />
                <AppSectionsList />
              </Card>
              <Box mt={2}>
                <AppPaymentMethodsList />
              </Box>
              <Box mt={2}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <AppCancelledOrders />
                  </Grid>
                  <Grid item xs={6}>
                    <AppRefundedSales />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {!(searchParams.get('start') && searchParams.get('end')) && (
              <Grid item xs={12}>
                <AppDailySalesChart />
              </Grid>
            )}

            <Grid item xs={12}>
              <AppLastOrdersList />
            </Grid>
          </Grid>
        </Container>
      </Page>
    </ReportProvider>
  );
}
